import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export interface WorkflowStatus {
  current_state: string;
  available_transitions: string[];
  workflow_metadata?: {
    district_data_qc_approval?: Array<{
      district_data_qc_approved_at: string;
      district_data_qc_approved_by: string;
      district_data_qc_approved_by_role: string;
    }>;
    district_data_qc_rejection?: Array<{
      district_data_qc_rejected_at: string;
      district_data_qc_rejected_by: string;
      district_data_qc_rejected_reason: string;
      district_data_qc_rejected_by_role: string;
    }>;
  };
}

export interface WorkflowTransitionRequest {
  transition_name:
    | 'district_data_qc_approval'
    | 'district_data_qc_rejection'
    | 'mark_data_upload_done'
    | 'unlock_data_upload'
    | 'approve_transcript_selection'
    | 'reject_transcript_selection';
}

export interface WorkflowTransitionResponse {
  workflow_type: string;
  previous_state: string;
  current_state: string;
  transition_name: string;
  workflow_metadata: Record<string, any>;
  updated_on: string;
  last_updated_by: string;
}

export interface SchoolWorkflowStatuses {
  focus_group_report: string | null;
  transcript_selection: string | null;
  school_report: string | null;
  quote_selection: string | null;
  survey_report: string | null;
  sjw_photo_upload: string | null;
}

export interface WorkflowStatusesResponse {
  schools: Record<string, SchoolWorkflowStatuses>;
}

const getDataIngestionWorkflowStatus = async (eoaId: string, access_token: string): Promise<WorkflowStatus> => {
  const response = await axios.get<WorkflowStatus>(
    `${BASE_URL}/eoa/${eoaId}/workflows/district_data_ingestion/status`,
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  );

  return response.data;
};

const executeWorkflowTransition = async (
  eoaId: string,
  access_token: string,
  transition_name: string,
  metadata?: Record<string, any>
): Promise<WorkflowTransitionResponse> => {
  const requestBody: any = { transition_name };
  if (metadata) {
    requestBody.workflow_metadata = metadata;
  }

  const response = await axios.post<WorkflowTransitionResponse>(
    `${BASE_URL}/eoa/${eoaId}/workflows/district_data_ingestion/transitions`,
    requestBody,
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data;
};

const getTranscriptSelectionWorkflowStatus = async (
  eoaId: string,
  schoolId: string,
  access_token: string
): Promise<WorkflowStatus> => {
  const response = await axios.get<WorkflowStatus>(
    `${BASE_URL}/eoa/${eoaId}/workflows/transcript_selection/${schoolId}/status`,
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  );

  return response.data;
};

const executeTranscriptSelectionWorkflowTransition = async (
  eoaId: string,
  schoolId: string,
  access_token: string,
  transition_name: string,
  metadata?: Record<string, any>
): Promise<WorkflowTransitionResponse> => {
  const requestBody: any = { transition_name };
  if (metadata) {
    requestBody.workflow_metadata = metadata;
  }

  const response = await axios.post<WorkflowTransitionResponse>(
    `${BASE_URL}/eoa/${eoaId}/workflows/transcript_selection/${schoolId}/transitions`,
    requestBody,
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data;
};

const getSchoolReportWorkflowStatus = async (
  eoaId: string,
  schoolId: string,
  access_token: string
): Promise<WorkflowStatus> => {
  const response = await axios.get<WorkflowStatus>(
    `${BASE_URL}/eoa/${eoaId}/workflows/school_report/${schoolId}/status`,
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  );

  return response.data;
};

const executeSchoolReportWorkflowTransition = async (
  eoaId: string,
  schoolId: string,
  access_token: string,
  transition_name: string,
  metadata?: Record<string, any>
): Promise<WorkflowTransitionResponse> => {
  const requestBody: any = { transition_name };
  if (metadata) {
    requestBody.workflow_metadata = metadata;
  }

  const response = await axios.post<WorkflowTransitionResponse>(
    `${BASE_URL}/eoa/${eoaId}/workflows/school_report/${schoolId}/transitions`,
    requestBody,
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data;
};

export interface GenerateSchoolReportResponse {
  school_id: string;
  current_state: string;
  message: string;
}

export interface SchoolReportResponse {
  id: string;
  school_id: string;
  eoa_id: string;
  presentation_id: string;
  presentation_name: string;
  google_slides_url: string;
  pdf_download_url: string;
  created_on: string;
  generated_at: string;
  created_by: string;
  is_latest: boolean;
}

const generateSchoolReport = async (
  eoaId: string,
  schoolId: string,
  access_token: string
): Promise<GenerateSchoolReportResponse> => {
  const response = await axios.post<GenerateSchoolReportResponse>(
    `${BASE_URL}/eoa/${eoaId}/schools/${schoolId}/generate_school_report`,
    {},
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data;
};

const getSchoolReport = async (
  eoaId: string,
  schoolId: string,
  access_token: string
): Promise<SchoolReportResponse> => {
  const response = await axios.get<SchoolReportResponse>(`${BASE_URL}/eoa/${eoaId}/schools/${schoolId}/school_report`, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });

  return response.data;
};

const getQuoteSelectionWorkflowStatus = async (
  eoaId: string,
  schoolId: string,
  access_token: string
): Promise<WorkflowStatus> => {
  const response = await axios.get<WorkflowStatus>(
    `${BASE_URL}/eoa/${eoaId}/workflows/quote_selection/${schoolId}/status`,
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  );

  return response.data;
};

const executeQuoteSelectionWorkflowTransition = async (
  eoaId: string,
  schoolId: string,
  access_token: string,
  transition_name: string,
  metadata?: Record<string, any>
): Promise<WorkflowTransitionResponse> => {
  const requestBody: any = { transition_name };
  if (metadata) {
    requestBody.workflow_metadata = metadata;
  }

  const response = await axios.post<WorkflowTransitionResponse>(
    `${BASE_URL}/eoa/${eoaId}/workflows/quote_selection/${schoolId}/transitions`,
    requestBody,
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data;
};

const COURSE_MAPPING_PRESET_VIEW_WORKFLOW = 'course_mapping_preset_view';
const MARK_PRESET_VIEWED_TRANSITION = 'mark_viewed';
export const PRESET_NOT_VIEWED_STATE = 'NOT_VIEWED';

const getCourseMappingPresetViewStatus = async (
  eoaId: string,
  presetId: string,
  access_token: string
): Promise<WorkflowStatus> => {
  const response = await axios.get<WorkflowStatus>(
    `${BASE_URL}/eoa/${eoaId}/workflows/${COURSE_MAPPING_PRESET_VIEW_WORKFLOW}/${presetId}/status`,
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    }
  );

  return response.data;
};

const markCourseMappingPresetViewed = async (
  eoaId: string,
  presetId: string,
  access_token: string
): Promise<WorkflowTransitionResponse> => {
  const response = await axios.post<WorkflowTransitionResponse>(
    `${BASE_URL}/eoa/${eoaId}/workflows/${COURSE_MAPPING_PRESET_VIEW_WORKFLOW}/${presetId}/transitions`,
    { transition_name: MARK_PRESET_VIEWED_TRANSITION },
    {
      headers: {
        Authorization: `Bearer ${access_token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  return response.data;
};

const getWorkflowStatuses = async (eoaId: string, access_token: string): Promise<WorkflowStatusesResponse> => {
  const response = await axios.get<WorkflowStatusesResponse>(`${BASE_URL}/eoa/${eoaId}/workflows/workflow-statuses`, {
    headers: {
      Authorization: `Bearer ${access_token}`,
    },
  });

  return response.data;
};

export {
  getDataIngestionWorkflowStatus,
  executeWorkflowTransition,
  getTranscriptSelectionWorkflowStatus,
  executeTranscriptSelectionWorkflowTransition,
  getSchoolReportWorkflowStatus,
  executeSchoolReportWorkflowTransition,
  generateSchoolReport,
  getSchoolReport,
  getQuoteSelectionWorkflowStatus,
  executeQuoteSelectionWorkflowTransition,
  getCourseMappingPresetViewStatus,
  markCourseMappingPresetViewed,
  getWorkflowStatuses,
};
